import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Breadcrumbs,
  Link,
  Box,
  Alert,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import { NavigateNext } from '@mui/icons-material';
import AcademicQuestionPaperForm from './AcademicQuestionPaperForm';
import { academicQuestionPaperApi } from './academicQuestionPaperApi';

const EditQuestionPaper = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [questionPaper, setQuestionPaper] = useState(null);
  const [fetchLoading, setFetchLoading] = useState(true);

  useEffect(() => {
    fetchQuestionPaper();
  }, [id]);

  const fetchQuestionPaper = async () => {
    try {
      const response = await academicQuestionPaperApi.getById(id);
      if (response.success && response.data) {
        setQuestionPaper(response.data);
      } else {
        setError('Failed to load question paper data');
      }
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Failed to load question paper';
      setError(message);
    } finally {
      setFetchLoading(false);
    }
  };

  const handleSubmit = async (data) => {
    setLoading(true);
    setError(null);
    try {
      await academicQuestionPaperApi.update(id, data);
      setSuccess(true);
      setTimeout(() => {
        navigate('/question-papers');
      }, 1500);
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Failed to update question paper';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/question-papers');
  };

  if (fetchLoading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error && !questionPaper) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Breadcrumbs
        separator={<NavigateNext fontSize="small" />}
        aria-label="breadcrumb"
        sx={{ mb: 3 }}
      >
        <Link color="inherit" href="/">
          Home
        </Link>
        <Link color="inherit" href="/question-papers">
          Question Papers
        </Link>
        <Typography color="text.primary">Edit</Typography>
      </Breadcrumbs>

      <Typography variant="h4" gutterBottom>
        Edit Question Paper
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Update the details of the question paper.
      </Typography>

      <AcademicQuestionPaperForm
        initialData={questionPaper}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        loading={loading}
      />

      <Snackbar
        open={success}
        autoHideDuration={3000}
        onClose={() => setSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccess(false)}>
          Question paper updated successfully!
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default EditQuestionPaper;